import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, gbp } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const { site } = useSite();
  const emailLive = site?.email_live === true;
  const path = window.location.pathname.split("/");
  const orderNumber = path[path.indexOf("order") + 1];
  const [order, setOrder] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let poll = null;
    (async () => {
      const token = params.get("t");
      if (token) {
        try {
          // exchange the one-time link token for a restricted session FIRST
          await api.post(`/public/orders/${orderNumber}/access`, { token });
          window.history.replaceState({}, "", `/order/${orderNumber}`);
        } catch {
          if (!cancelled) setAccessDenied(true);
          return;
        }
      }
      try {
        const res = await api.get(`/public/orders/${orderNumber}`);
        if (!cancelled) setOrder(res.data);
      } catch {
        if (!cancelled) setAccessDenied(true);
        return;
      }
      // keep payment status fresh until it settles
      poll = setInterval(async () => {
        try {
          const res = await api.get(`/public/orders/${orderNumber}`);
          if (!cancelled) setOrder(res.data);
        } catch {
          /* keep polling quietly */
        }
      }, 4000);
    })();
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  if (accessDenied) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="order-access-denied">
        <h1 className="font-serif text-3xl font-bold text-cream-text">Order not found</h1>
        <p className="mt-3 text-sm text-cream-muted">
          Order pages need the secure link from your confirmation. If you've lost it, contact us and we'll help.
        </p>
        <Link to="/contact" className="mt-6 inline-block rounded-full bg-gold px-7 py-3 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="order-contact-link">
          Contact Us
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto flex max-w-3xl items-center justify-center gap-3 px-4 py-24 text-sm text-cream-muted" data-testid="order-loading">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your order…
      </div>
    );
  }

  const awaiting = order.payment_status === "awaiting_payment" || order.payment_status === "processing" || order.payment_status === "failed";
  const unpaidCollection = order.payment_status === "unpaid_on_collection";

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16" data-testid="order-confirmation-page">
      <Reveal>
        <div className="rounded-3xl bg-cream p-6 text-cream-darktext sm:p-10">
          <CheckCircle2 className="h-12 w-12 text-gold" aria-hidden="true" />
          <h1 className="mt-4 font-serif text-3xl font-bold" data-testid="order-heading">
            Thank you, {order.customer_first_name}!
          </h1>
          <p className="mt-2 text-sm text-cream-darkmuted">
            Order <strong data-testid="order-number">{order.order_number}</strong> ·{" "}
            {order.fulfilment_method === "collection" ? "Collection" : "Local delivery"}
            {order.slot && (
              <>
                {" "}on {new Date(order.slot.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/London" })},{" "}
                {order.slot.start_time}–{order.slot.end_time}
              </>
            )}
          </p>

          {order.payment_status === "paid" && (
            <p className="mt-4 rounded-xl bg-green-100 p-3 text-sm font-semibold text-green-800" data-testid="payment-paid-note">
              Payment confirmed — we're baking your order fresh.
            </p>
          )}
          {awaiting && (
            <p className="mt-4 rounded-xl bg-amber-100 p-3 text-sm font-semibold text-amber-800" data-testid="payment-pending-note">
              Your payment is being confirmed — this page updates automatically.
            </p>
          )}
          {unpaidCollection && (
            <p className="mt-4 rounded-xl bg-amber-100 p-3 text-sm font-semibold text-amber-800" data-testid="payment-on-collection-note">
              Pay on collection: please have {gbp(order.amount_due_pence)} ready when you pick up.
            </p>
          )}

          <ul className="mt-6 space-y-2 text-sm" data-testid="order-items">
            {order.items.map((item, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>
                  {item.qty} × {item.name}
                  {Object.keys(item.option_values || {}).length > 0 && (
                    <span className="block text-xs text-cream-darkmuted">{Object.values(item.option_values).join(" · ")}</span>
                  )}
                  {item.allergen_declaration_status === "none_of_14_as_ingredients" ? (
                    <span className="block text-xs text-cream-darkmuted" data-testid={`order-item-allergens-${i}`}>No UK 14 regulated allergens are used as ingredients.</span>
                  ) : item.allergens?.length > 0 ? (
                    <span className="block text-xs capitalize text-cream-darkmuted" data-testid={`order-item-allergens-${i}`}>Contains: {item.allergens.join(", ")}.</span>
                  ) : null}
                  {item.may_contain?.length > 0 && (
                    <span className="block text-xs capitalize text-amber-500/90" data-testid={`order-item-may-contain-${i}`}>May contain: {item.may_contain.join(", ")}.</span>
                  )}
                </span>
                <span className="font-semibold">{gbp(item.line_total_pence)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-1 border-t border-cream-darkmuted/20 pt-4 text-sm">
            <p className="flex justify-between"><span>Subtotal</span><span>{gbp(order.subtotal_pence)}</span></p>
            <p className="flex justify-between"><span>{order.fulfilment_method === "delivery" ? "Delivery" : "Collection"}</span><span>{order.delivery_fee_pence ? gbp(order.delivery_fee_pence) : "Free"}</span></p>
            <p className="flex justify-between pt-1 font-serif text-xl font-bold"><span>Total</span><span data-testid="order-total">{gbp(order.total_pence)}</span></p>
          </div>

          {order.collection_note && (
            <p className="mt-4 text-xs text-cream-darkmuted" data-testid="collection-note">{order.collection_note}</p>
          )}
          <p className="mt-6 text-xs text-cream-darkmuted" data-testid="order-email-note">
            {emailLive
              ? "A confirmation email is on its way to you. Keep this page link safe — it's your order view."
              : "Keep this page link safe — it's your order view. We'll confirm the details with you directly."}
          </p>
        </div>
      </Reveal>
    </div>
  );
}
